import React from 'react';

import { usePageStore } from '@client/store/pageStore/context/pageContext';
import { Popper, TextField, Typography } from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import { styled } from '@mui/material/styles';

import AutocompleteOptionWithChart from './AutocompleteOptionWithChart';

export interface ListItem {
    id: string;
    name: string;
    locked?: unknown;
}

interface ItemListAutocompleteProps {
    itemList: ListItem[] | null;
    setSelectedItem: (item: ListItem | null) => void;
    selectedItem: ListItem | null;
    onClickEdit: () => void;
    onClickView: () => void;
}

const ItemListAutocomplete = ({ itemList, setSelectedItem, selectedItem, onClickEdit, onClickView }: ItemListAutocompleteProps) => {
    const {
        pageState: { pageConfig }
    } = usePageStore();

    function renderOption(props: React.HTMLAttributes<HTMLLIElement>, option: ListItem) {
        function onDoubleClick() {
            if (!selectedItem) {
                return;
            }
            if (selectedItem.locked) {
                onClickView();
            } else {
                onClickEdit();
            }
        }

        return (
            <li {...props}>
                <AutocompleteOptionWithChart errorCodes={pageConfig.errorCodes} option={option} size="large" onDoubleClick={onDoubleClick} />
            </li>
        );
    }

    const StyledPopper = styled(Popper)({
        transform: 'none !important'
    });

    function getOptionLabel(option: ListItem) {
        return option.name;
    }

    function isOptionEqualToValue(option: ListItem, value: ListItem) {
        return option.id === value.id;
    }

    function onChange(_event: React.SyntheticEvent, item: ListItem | null) {
        setSelectedItem(item);
    }

    function renderInput(params: object) {
        return <TextField {...(params as React.ComponentProps<typeof TextField>)} label={pageConfig.autoCompletePlaceholder} variant="outlined" />;
    }

    if (!itemList || (!itemList.length && !pageConfig.emptyMessage)) {
        return null;
    }

    return itemList.length ? (
        <Autocomplete
            ListboxProps={{ style: { maxHeight: '100%' } }}
            value={selectedItem}
            options={itemList}
            getOptionLabel={getOptionLabel}
            isOptionEqualToValue={isOptionEqualToValue}
            onChange={onChange}
            renderInput={renderInput}
            open
            disablePortal
            fullWidth={true}
            PopperComponent={StyledPopper}
            renderOption={renderOption}
        />
    ) : (
        <Typography variant="subtitle2">{pageConfig.emptyMessage}</Typography>
    );
};

export default ItemListAutocomplete;
